import { injectable, inject } from 'inversify'
import Debug from 'debug';
import { StateMachine } from '@/app/shared/state-machine'
import { InteractionStateStoreModule, InteractionStoreModuleSymbol } from '../../store'
import { InteractionStateMachineData, InteractionStateMachinePhase } from './interaction-state-machine-data'
import { InteractionStateService, InteractionStateServiceSymbol } from '../interaction-state-service';
import * as States from './states'
import { InteractionStateChooserFactory, InteractionStateChooserFactorySymbol } from '../../api';

const debug = Debug('interaction-state-machine')

type State<TState> = States.InitialState<TState> | States.WaitingForRemoteState<TState> | States.LoadedState<TState>

export const InteractionStateMachineSymbol = Symbol.for('InteractionStateMachine')

@injectable()
export class InteractionStateMachine<TState> {

  private stateMachine: StateMachine<State<TState>, InteractionStateMachinePhase>
  private data: InteractionStateMachineData

  onResetComponent?: (state: TState | null) => void

  constructor(
    @inject(InteractionStoreModuleSymbol)
    private stateStore: InteractionStateStoreModule,
    @inject(InteractionStateServiceSymbol)
    private stateService: InteractionStateService,
    @inject(InteractionStateChooserFactorySymbol)
    private stateChooserFactory: InteractionStateChooserFactory<TState>) {

    this.stateMachine = new StateMachine<State<TState>, InteractionStateMachinePhase>('interaction-state-machine')
    this.stateMachine.addStateChangedListener((phase) => {
      debug('state changed', this.data.id, this.data.type, phase)
    })
  }

  initialize(id: string, type: string) {

    debug('initialize', id, type)
    
    this.data = new InteractionStateMachineData(id, type)
    const initialState = new States.InitialState<TState>(this.stateStore, this.data)
    this.stateMachine.changeState(initialState, {
      onWaitingForRemoteState: () => this.gotoWaitingForRemoteState(),
      onLoaded: () => this.gotoRemoteStateLoaded(),
      onResetComponent: (state) => this.fireResetComponent(state)
    })
  }

  private gotoWaitingForRemoteState() {
    const state = new States.WaitingForRemoteState<TState>(this.stateStore,
                                                           this.stateService,
                                                           this.data,
                                                           this.stateChooserFactory)
    this.stateMachine.changeState(state, {
      onLoaded: () => this.gotoRemoteStateLoaded(),
      onResetComponent: (state) => this.fireResetComponent(state)
    })
  }

  private fireResetComponent(state: TState | null) {
    if (this.onResetComponent) {
      this.onResetComponent(state)
    }
  }

  private gotoRemoteStateLoaded() {
    const state = new States.LoadedState<TState>(this.stateService,
                                            this.data)
    this.stateMachine.changeState(state, {
    })
  }

  start() {
    debug('start', this.data.id, this.data.type)

    this.stateMachine.executeAction('start')
  }

  cancel() {
    debug('cancel', this.data.id, this.data.type)

    this.stateMachine.executeAction('cancel')
  }

  updateInteractionState(state: TState) {
    debug('updateInteractionState', state)

    this.stateMachine.executeAction('updateInteractionState', state)
  }

  updateInteractionResult(progress: number, status: 'incomplete' | 'completed') {
    debug('updateInteractionResult', progress, status)

    this.stateMachine.executeAction('updateInteractionResult', progress, status)
  }
}



